import { useState } from "react";
import { X, UploadCloud } from "lucide-react";
import { api, mediaUrl } from "@/lib/oraya-api";
import { toast } from "sonner";

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium tracking-wide text-oraya-slate uppercase">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls = "w-full bg-oraya-black border border-white/10 rounded-lg px-3 py-2.5 text-oraya-mist focus:border-oraya-accent focus:outline-none transition-colors text-sm";

export function TextInput({ value, onChange, testid, ...rest }) {
  return <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} />;
}

export function TextArea({ value, onChange, rows = 5, testid, ...rest }) {
  return <textarea className={`${inputCls} resize-y`} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} />;
}

export function SelectInput({ value, onChange, options, testid }) {
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map((o) => <option key={o.value} value={o.value} className="bg-oraya-graphite">{o.label}</option>)}
    </select>
  );
}

export function ImageUpload({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-4">
      {value ? (
        <img src={mediaUrl(value)} alt="preview" className="w-20 h-20 rounded-lg object-cover border border-white/10" />
      ) : (
        <div className="w-20 h-20 rounded-lg border border-dashed border-white/15 flex items-center justify-center text-oraya-slate/40">
          <UploadCloud className="w-6 h-6" />
        </div>
      )}
      <div className="flex flex-col gap-2">
        <label className="cursor-pointer text-sm text-oraya-mist glass-medium rounded-lg px-4 py-2 hover:border-white/20 transition-colors inline-flex items-center gap-2">
          <UploadCloud className="w-4 h-4" /> {busy ? "Uploading…" : "Upload image"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={testid} />
        </label>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs text-oraya-slate hover:text-red-400 text-left">Remove</button>}
      </div>
    </div>
  );
}

export function AdminModal({ title, onClose, children, onSave, saving, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid={testid}>
      <div className="bg-oraya-graphite border border-white/10 rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <h3 className="display text-lg font-medium text-oraya-mist">{title}</h3>
          <button onClick={onClose} className="text-oraya-slate hover:text-oraya-mist" data-testid="modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto flex flex-col gap-4">{children}</div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/[0.06]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-oraya-slate hover:text-oraya-mist">Cancel</button>
          <button onClick={onSave} disabled={saving} data-testid="modal-save" className="px-5 py-2 text-sm rounded-lg bg-oraya-mist text-oraya-black font-medium hover:bg-white disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    New: "bg-oraya-accent/20 text-oraya-accent", "In Review": "bg-yellow-500/15 text-yellow-300",
    Contacted: "bg-blue-500/15 text-blue-300", Closed: "bg-white/10 text-oraya-slate",
    Reviewing: "bg-yellow-500/15 text-yellow-300", Shortlisted: "bg-blue-500/15 text-blue-300",
    Rejected: "bg-red-500/15 text-red-300", Hired: "bg-emerald-500/15 text-emerald-300",
    active: "bg-emerald-500/15 text-emerald-300", coming_soon: "bg-yellow-500/15 text-yellow-300",
    future: "bg-white/10 text-oraya-slate", published: "bg-emerald-500/15 text-emerald-300",
    draft: "bg-white/10 text-oraya-slate", open: "bg-emerald-500/15 text-emerald-300", closed: "bg-white/10 text-oraya-slate",
  };
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs ${map[status] || "bg-white/10 text-oraya-slate"}`}>{String(status).replace("_", " ")}</span>;
}
